import { ActionType } from '@/actions/actiontypes';
import AppModal from '@/components/common/AppModal';
import SimpleSelect from '@/components/objects/SimpleSelect';
import {
    ExportEnumFormat,
    MapExportEnumFormatToDisplayName,
} from '@/domain/ExportEnumFormat';
import { MapProjeksjonToDisplayName, Projeksjon } from '@/domain/Projeksjon';
import { statePreferences } from '@/selectors/selectors';
import { PreferencesState } from '@/state';
import { useDispatch, useSelector } from '@/state/store';
import * as React from 'react';
import { useState } from 'react';

type ExportPreferences = Pick<PreferencesState, 'exportProjection' | 'exportEnumFormat'>;

interface ExportSettingsDialogProps {
    isOpen: boolean;
    setIsOpen: (isOpen: boolean) => void;
}

export const ExportSettingsDialog: React.FC<ExportSettingsDialogProps> = ({
    isOpen,
    setIsOpen,
}) => {
    if (!isOpen) return null;

    return <OpenExportSettingsDialog setIsOpen={setIsOpen} />;
};

const OpenExportSettingsDialog = ({
    setIsOpen,
}: Pick<ExportSettingsDialogProps, 'setIsOpen'>) => {
    const preferences = useSelector(statePreferences);
    const dispatch = useDispatch();
    const [selectedPreferences, setSelectedPreferences] = useState<ExportPreferences>(
        () => ({
            exportProjection: preferences.exportProjection,
            exportEnumFormat: preferences.exportEnumFormat,
        })
    );

    const handleProjectionSelect = (projection: Projeksjon) => {
        setSelectedPreferences(prev => ({
            ...prev,
            exportProjection: projection,
        }));
    };

    const handleEnumFormatSelect = (enumFormat: ExportEnumFormat) => {
        setSelectedPreferences(prev => ({
            ...prev,
            exportEnumFormat: enumFormat,
        }));
    };

    const handleClose = () => {
        dispatch({
            type: ActionType.PREFERENCES_UPDATE,
            ...selectedPreferences,
        });
    };

    return (
        <AppModal
            show
            onClose={() => {
                handleClose();
                setIsOpen(false);
            }}
            title={'Eksportinnstillinger'}
        >
            <div className="c-preferences__toggle-content">
                {/* General settings */}
                <div className="c-export-settings-section">
                    <h3 className="c-export-settings-section__title">
                        Generelle innstillinger
                    </h3>
                    <SimpleSelect
                        label="Velg kartprojeksjon"
                        options={Object.values(Projeksjon)}
                        optionLabel={option => MapProjeksjonToDisplayName.get(option)}
                        value={selectedPreferences['exportProjection']}
                        onChange={handleProjectionSelect}
                    />
                </div>

                {/* SOSI settings */}
                <div className="c-export-settings-section">
                    <h3 className="c-export-settings-section__title">
                        SOSI-innstillinger
                    </h3>
                    <SimpleSelect
                        label="Velg format for tillatte verdier (enum) på SOSI-fil"
                        options={Object.values(ExportEnumFormat)}
                        optionLabel={option =>
                            MapExportEnumFormatToDisplayName.get(option)
                        }
                        value={selectedPreferences['exportEnumFormat']}
                        onChange={handleEnumFormatSelect}
                    />
                </div>
            </div>
        </AppModal>
    );
};

export default ExportSettingsDialog;
